function _await(value, then, direct) {
  if (direct) {
    return then ? then(value) : value;
  }

  if (!value || !value.then) {
    value = Promise.resolve(value);
  }

  return then ? value.then(then) : value;
}

const AuthToken = require('../auth/auth-token');

function _call(body, then, direct) {
  if (direct) {
    return then ? then(body()) : body();
  }

  try {
    var result = Promise.resolve(body());
    return then ? result.then(then) : result;
  } catch (e) {
    return Promise.reject(e);
  }
}

const AuthProvider = require('../auth/auth-provider');

const MetaRepo = require('./meta-repo');

const User = require('../entities/user');

const {
  poll
} = require('../utils/poll');
/*
uri = get(admin-api/auth/meta) + "&identity_provider=Google"
token = get(uri) // redirect to google sign in, do their auth flow, redirect back to app with token in path
get(admin-api/resource, headers: { Authorization: token });
 */


class AuthRepo {
  constructor(client
  /*: ApiClient*/
  , authProvider
  /*: AuthProvider*/
  ) {
    this.client = client;
    this.authProvider = authProvider;
    this.meta = new MetaRepo(client);
  }
  /*
  Google Auth Flow:
   - Get new auth URL
  - Load it in the browser
  - Login through google
  - On return to the redirectUri, parse the token, and finalize
  - Load user details
   */


  oauthGetAuthUrl(type
  /*: 'code' | 'token'*/
  , redirectUrl
  /*: string*/
  , state) {
    return this.meta.get().then(meta => {
      const opts = [`client_id=${meta.oauth.clientId}`, `redirect_uri=${encodeURIComponent(redirectUrl)}`, `response_type=${type}`, state ? `state=${state}` : ''].join('&');
      return `${meta.oauth.baseUri}/login?${opts}`;
    });
  }

  oauthSetToken({
    /*: { accessToken: string, refreshToken: string }*/

    accessToken,
    refreshToken
  }) {
    this.authProvider.setRaw(accessToken, refreshToken);
  } // This is what's called in the web app with the Okta code


  oauthSetCode(code, redirectUrl, context) {
    const _this = this;

    return _call(function () {
      return _await(_this.exchangeOktaCode(code, redirectUrl, context), function (token) {
        _this.oauthSetToken(token);
      });
    });
  }

  exchangeOktaCode(code, redirectUri, context) {
    const req = new this.client.Request(`/users/auth/login/okta`, {
      code,
      redirectUri,
      context
    });
    req.authType = req.AUTH_TYPES.AUTH_FORBIDDEN;
    return this.client.post(req);
  }

  oauthExchangeCode(code
  /*: string*/
  , redirectUrl
  /*: string*/
  ) {
    return this.meta.get().then(meta => {
      const url = `${meta.oauth.cognito.baseUri}/oauth2/token`;
      const opt = {
        method: 'POST',
        headers: {
          'Content-type': 'application/x-www-form-urlencoded'
        },
        body: ['grant_type=authorization_code', `client_id=${meta.oauth.cognito.clientId}`, `code=${code}`, `redirect_uri=${encodeURIComponent(redirectUrl)}`].join('&')
      };
      return fetch(url, opt);
    }).then(res => res.json()).then(tokens => {
      this.authProvider.setTokens(new AuthToken(tokens.id_token, tokens.refresh_token));
      return tokens;
    });
  }
  /*
  login(): Promise<?User> {
    return this.client
      .get(new this.client.Request('/users/auth/login')) // ensures this is always async
      .then((res) => {
        this.authProvider.setTokens('faketoken');
        return res;
      });
  }
  */


  logout()
  /*: Promise<boolean>*/
  {
    this.authProvider.logout();
    return new Promise((res, err) => setTimeout(() => res(true), 0));
  }

  profile()
  /*: Promise<?User>*/
  {
    const tokens = this.authProvider.getTokens();
    if (!tokens) return Promise.resolve(null);
    const req = new this.client.Request('/self', {}, ['id', 'email', 'name', 'enabled']);

    req.parse = json => new User(json.id, json.email, json.name);

    return this.client.get(req);
  }

  loginSsoRequest(provider
  /*: string*/
  , action
  /*: Array<string>*/
  , payload
  /*: string*/
  , signature
  /*: string*/
  , oktaCode
  /*: string*/
  , oktaRedirectUri
  /*: string*/
  )
  /*: Promise<{ accessToken: string, refreshToken: string }>*/
  {
    const params = {
      provider,
      action,
      payload,
      signature
    };

    if (action.includes('link-user')) {
      params.oktaCode = oktaCode;
      params.oktaRedirectUri = oktaRedirectUri;
    }

    const req = new this.client.Request('/users/auth/login/sso', params);
    req.authType = req.AUTH_TYPES.AUTH_OPTIONAL;
    return this.client.post(req);
  }
  /* eslint-disable-next-line max-len */


  appAuthorize(provider
  /*: string*/
  , payload
  /*: string*/
  , signature
  /*: string*/
  )
  /*: Promise<{ businessId: string, locationId: string }>*/
  {
    const req = new this.client.Request(`/self/auth/app/authorize`, {
      provider,
      payload,
      signature
    });
    return this.client.post(req);
  }

  ssoLogin({
    /*: {
        provider: string,
        action: Array<string>,
        payload: string,
        signature: string
      }*/

    provider,
    action,
    payload,
    signature
  }) {
    const _this2 = this;

    return _call(function () {
      // SSO Login
      return _await(_this2.loginSsoRequest(provider, action, payload, signature), function (token) {
        _this2.oauthSetToken(token); // wait for Role to be assigned...


        return poll( // eslint-disable-next-line arrow-body-style
        () => {
          return _this2.profile().then(() => true, () => false);
        }, 1000, 30000, () => {
          throw new Error('Unable to authorize');
        });
      });
    });
  }

  linkAccount(provider
  /*: string*/
  , action
  /*: string*/
  , payload
  /*: string*/
  , signature
  /*: string*/
  , oktaCode
  /*: string*/
  , oktaRedirectUri
  /*: string*/
  ) {
    return this.loginSsoRequest(provider, action, payload, signature, oktaCode, oktaRedirectUri);
  }

  unlinkSelf(provider
  /*: string*/
  )
  /*: Promise<void>*/
  {
    const path = '/self/auth/unlink';
    const req = new this.client.Request(path, {
      provider
    });
    return this.client.post(req);
  }

}

module.exports = AuthRepo;